import { useCallback, useEffect, useRef } from "react";

import { Callout, Icon } from "@blueprintjs/core";
import { Ellipsis } from "lucide-react";

import { roundDecimal } from "@/components/helpers/number-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LIGHT_GRAY, SUBTEXT_COLOR } from "@/css/constants";
import { RoleplaySessionTranscriptFragment } from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";

import { ChatMessage } from "./ChatMessage";
import {
  copyTranscriptToClipboard,
  downloadTranscriptAsSrt,
} from "./utils/transcript-export";

const FIXED_HEADER_HEIGHT = 160;

interface ChatTimelineProps {
  messagesData: RoleplaySessionTranscriptFragment[];
  onSelectedTime?: (seconds: number) => void;
  selectedMessage?: RoleplaySessionTranscriptFragment;
  onSelectMessage?: (message: RoleplaySessionTranscriptFragment) => void;
  seekTime: number;
  duration: number;
  scenarioName: string;
  user: {
    name?: string | null;
    email?: string | null;
  };
}

export function ChatTimeline({
  messagesData,
  onSelectedTime,
  onSelectMessage,
  selectedMessage,
  seekTime,
  duration,
  scenarioName,
  user,
}: ChatTimelineProps) {
  // Create refs for message elements
  const messageRefs = useRef<{ [id: string]: HTMLDivElement | null }>({});
  // Track the last selected message ID to detect external changes
  const lastSelectedIdRef = useRef<string | undefined>(selectedMessage?.id);

  const handleTimeClick = useCallback(
    (seconds: number) => {
      onSelectedTime?.(roundDecimal(seconds, 2));
    },
    [onSelectedTime],
  );

  const handleMessageClick = useCallback(
    (msg: RoleplaySessionTranscriptFragment) => {
      onSelectMessage?.(msg);
      // Update our ref to track that this selection happened internally
      lastSelectedIdRef.current = msg.id;
    },
    [onSelectMessage],
  );

  useEffect(() => {
    if (!selectedMessage || !selectedMessage.id) return;

    // If the selected message changed and it wasn't from our internal click handler
    if (selectedMessage.id !== lastSelectedIdRef.current) {
      const messageElement = messageRefs.current[selectedMessage.id];

      if (messageElement) {
        // Account for fixed header when scrolling
        const topOffset = messageElement.getBoundingClientRect().top;

        // Scroll the window with smooth behavior and offset
        window.scrollTo({
          top: window.scrollY + topOffset - FIXED_HEADER_HEIGHT,
          behavior: "smooth",
        });
      }

      // Update our ref to track this selection
      lastSelectedIdRef.current = selectedMessage.id;
    }
  }, [selectedMessage]);

  if (messagesData?.length === 0) {
    return null;
  }

  return (
    <Box
      className="bas round-corner"
      backgroundColor="white"
      px={24}
      py={3}
      alignItems="center"
      flexDirection={["column", "row"]}
      justifyContent="space-between"
      width={["100%", "auto"]}
      mt={16}
    >
      <Flex justifyContent="space-between" alignItems="center" width="100%">
        <Callout
          intent="none"
          title={undefined}
          className="w-fit"
          icon={<Icon icon="info-sign" color={SUBTEXT_COLOR} size={16} />}
          style={{
            marginBottom: 16,
            marginTop: 16,
            backgroundColor: LIGHT_GRAY,
            fontSize: 14,
          }}
        >
          <Box css={{ color: SUBTEXT_COLOR }}>
            The transcript may not be perfectly accurate.
          </Box>
        </Callout>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="bg-gray-200 hover:bg-gray-300 border-gray-200">
              <Ellipsis color={SUBTEXT_COLOR} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuItem
              onClick={() => copyTranscriptToClipboard(messagesData)}
            >
              Copy Transcript
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                downloadTranscriptAsSrt(
                  messagesData,
                  user.name || "User",
                  scenarioName,
                )
              }
            >
              Download Transcript
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Flex>
      {ensureNonNullArray(messagesData).map((msg) => {
        const { id } = msg;
        if (!id) return null;

        return (
          <Box
            key={id}
            ref={(el) => {
              messageRefs.current[id] = el;
            }}
          >
            <ChatMessage
              key={id}
              {...msg}
              seekTime={seekTime}
              duration={duration}
              isFeedbackActive={selectedMessage?.id === id}
              onClickItem={() => handleTimeClick(msg.secondsFromStart ?? 0)}
              onClickMessage={() => handleMessageClick(msg)}
            />
          </Box>
        );
      })}
    </Box>
  );
}
